/**
 * Collection Color Swatches
 * Enhances standard color filters with visual color swatches
 */

(function() {
  'use strict';

  // Color mapping - converts filter labels to hex colors
  const colorMap = {
    // Blues
    'blue': '#2563eb',
    'true blue': '#2563eb',
    'ice blue': '#60a5fa',
    'light blue': '#60a5fa',
    'powder blue': '#93c5fd',
    'navy': '#1e3a5f',
    'dark blue': '#1e40af',
    'sky blue': '#87ceeb',
    'baby blue': '#89c4f4',
    'navy blue': '#000080',
    'midnight blue': '#191970',
    'royal blue': '#4169e1',
    'sapphire': '#0f52ba',
    'duke blue': '#003366',
    'baylor blue': '#0058a8',
    
    // Blacks/Darks
    'black': '#1a1a1a',
    'black rollerball': '#1a1a1a',
    'midnight': '#0f172a',
    'stealth': '#18181b',
    'onyx': '#0f0f0f',
    'charcoal': '#36454f',
    'slate': '#708090',
    'ash': '#b2beb5',
    
    // Reds
    'red': '#dc2626',
    'cherry red': '#e11d48',
    'crimson': '#991b1b',
    'ruby': '#e0115f',
    'neon red': '#ff073a',
    'hot pink': '#ff69b4',
    
    // Greens
    'green': '#16a34a',
    'green rollerball': '#15803d',
    'emerald': '#059669',
    'peacock': '#0d9488',
    'teal': '#0d9488',
    'kiwi': '#84cc16',
    'mint': '#98ff98',
    'mint green': '#98ff98',
    'seafoam': '#9fe2bf',
    'seafoam green': '#9fe2bf',
    'spring green': '#00ff7f',
    'forest green': '#228b22',
    'pine': '#01796f',
    'jade': '#00a86b',
    'lime green': '#32cd32',
    'neon green': '#39ff14',
    
    // Purples
    'purple': '#7c3aed',
    'midnight purple': '#581c87',
    'violet': '#6d28d9',
    'amethyst': '#9966cc',
    'lavender': '#a78bfa',
    'swirl': '#8b5cf6',
    'cosmic': '#8b5cf6',
    
    // Pinks
    'pink': '#ec4899',
    'fuchsia': '#d946ef',
    'rose': '#f43f5e',
    'bubblegum': '#ff85a2',
    'blush': '#e8a0bf',
    'salmon': '#fa8072',
    'peach': '#ffdab9',
    'neon pink': '#ff10f0',
    
    // Oranges
    'orange': '#ea580c',
    'orange rollerball': '#f97316',
    'coral': '#fb923c',
    'neon orange': '#ff5f1f',
    
    // Yellows
    'yellow': '#eab308',
    'sunflower': '#facc15',
    'gold': '#d4af37',
    'lemon': '#fff44f',
    'butter': '#ffda79',
    'honey': '#f0e3a2',
    'caramel': '#c68e17',
    'neon yellow': '#ffff00',
    
    // Neutrals
    'grey': '#9ca3af',
    'gray': '#9ca3af',
    'stainless': '#a1a1aa',
    'silver': '#c0c0c0',
    'pearl': '#f3f4f6',
    'white': '#ffffff',
    'ivory': '#fafaf9',
    'cream': '#fdf6e3',
    'sand': '#c2b280',
    'tan': '#d2b48c',
    'camel': '#c19a6b',
    
    // Browns
    'brown': '#92400e',
    'chocolate': '#7b3f00',
    'coffee': '#6f4e37',
    
    // Metallics
    'brass': '#b8860b',
    'copper': '#b87333',
    'rose gold': '#e8b4b8',
    'bronze': '#cd7f32',
    'metallic gold': '#d4af37',
    'metallic silver': '#c0c0c0',
    'metallic bronze': '#cd7f32',
    'metallic copper': '#b87333',
    'reflect': '#d4af37',
    'mirror': '#e8e8e8',
    'chrome': '#c0c0c0',
    'graphite': '#585858',
    'matte': '#888888',
    'gloss': '#e8e8e8'
  };

  // Color swatch filter class
  class ColorSwatchFilter {
    constructor() {
      this.init();
    }

    init() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.enhanceFilters());
      } else {
        this.enhanceFilters();
      }
      
      // Also handle AJAX page loads (Shopify section rendering)
      document.addEventListener('section-updated', () => this.enhanceFilters());
      
      // Observe for dynamically loaded content
      this.observeChanges();
    }

    observeChanges() {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            this.enhanceFilters();
          }
        });
      });

      // Observe the facets container
      const facetsContainer = document.getElementById('FacetFiltersForm') || 
                              document.querySelector('.facets') ||
                              document.querySelector('[id*="FacetFiltersForm"]');
      
      if (facetsContainer) {
        observer.observe(facetsContainer, {
          childList: true,
          subtree: true
        });
      }
    }

    enhanceFilters() {
      // Find all filter groups
      const filterGroups = document.querySelectorAll('.facets__disclosure, details[id^="Details-"]');
      
      filterGroups.forEach(group => {
        const header = group.querySelector('summary, .facets__summary');
        if (!header) return;
        
        const headerText = header.textContent.toLowerCase();
        
        // Check if this is an ink color filter
        if (this.isColorFilter(headerText)) {
          this.convertToSwatches(group, headerText);
        }
      });
    }

    isColorFilter(text) {
      const colorKeywords = ['ink color', 'color', 'colour'];
      return colorKeywords.some(keyword => text.includes(keyword));
    }

    convertToSwatches(filterGroup, label) {
      // Don't convert if already converted
      if (filterGroup.classList.contains('color-swatches-enhanced')) {
        return;
      }

      const displayContainer = filterGroup.querySelector('.facets__display, .facets__display-vertical');
      if (!displayContainer) return;

      const list = displayContainer.querySelector('ul.facets__list, ul.facets__list--vertical');
      if (!list) return;

      // Get all filter values
      const filterItems = list.querySelectorAll('li.facets__item');
      if (filterItems.length === 0) return;

      // Mark as enhanced
      filterGroup.classList.add('color-swatches-enhanced', 'facets__filter-group--color');
      displayContainer.classList.add('facets__display--color-swatches');

      // Create swatches container
      const swatchesContainer = document.createElement('div');
      swatchesContainer.className = 'color-swatches';
      
      // Check if many colors, make scrollable
      if (filterItems.length > 8) {
        swatchesContainer.classList.add('color-swatches--scrollable');
      }

      // Convert each filter item to a swatch
      filterItems.forEach(item => {
        const label = item.querySelector('label, .facets__label');
        const input = item.querySelector('input[type="checkbox"]');
        if (!label || !input) return;

        const labelText = label.querySelector('.facet-checkbox__text-label, span');
        if (!labelText) return;

        const colorName = labelText.textContent.trim().toLowerCase();
        const colorHex = this.getColorHex(colorName);
        const isChecked = input.checked;
        const count = label.querySelector('.facet-checkbox__text');
        const countText = count ? count.textContent.match(/\((\d+)\)/)?.[1] || '' : '';

        // Create swatch element
        const swatchWrapper = document.createElement('div');
        swatchWrapper.className = 'color-swatch';
        
        swatchWrapper.innerHTML = `
          <input type="checkbox" 
                 id="${input.id}" 
                 name="${input.name}" 
                 value="${input.value}" 
                 ${isChecked ? 'checked' : ''}
                 ${input.disabled ? 'disabled' : ''}>
          <div class="color-swatch__circle" 
               data-color="${colorName}"
               style="background-color: ${colorHex}"
               title="${labelText.textContent.trim()}">
          </div>
          <span class="color-swatch__tooltip">${labelText.textContent.trim()}${countText ? ' (' + countText + ')' : ''}</span>
        `;

        // Add click handler
        const swatchCircle = swatchWrapper.querySelector('.color-swatch__circle');
        const checkbox = swatchWrapper.querySelector('input[type="checkbox"]');
        
        swatchCircle.addEventListener('click', (e) => {
          e.preventDefault();
          checkbox.checked = !checkbox.checked;
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
          
          // Trigger form submission for Shopify filtering
          const form = checkbox.closest('form');
          if (form) {
            const event = new Event('input', { bubbles: true });
            form.dispatchEvent(event);
          }
        });

        swatchesContainer.appendChild(swatchWrapper);
      });

      // Clear existing list
      list.style.display = 'none';
      
      // Insert swatches after the list
      displayContainer.insertBefore(swatchesContainer, list.nextSibling);

      // Add "Clear" link if there are active filters
      const activeCount = filterGroup.querySelectorAll('input[type="checkbox"]:checked').length;
      if (activeCount > 0) {
        const clearLink = document.createElement('a');
        clearLink.className = 'color-swatch-clear';
        clearLink.href = '#';
        clearLink.textContent = `Clear ${activeCount} selected`;
        clearLink.addEventListener('click', (e) => {
          e.preventDefault();
          filterGroup.querySelectorAll('input[type="checkbox"]:checked').forEach(input => {
            input.checked = false;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
          // Trigger form submission
          const form = input.closest('form');
          if (form) {
            const event = new Event('input', { bubbles: true });
            form.dispatchEvent(event);
          }
        });
        swatchesContainer.after(clearLink);
      }
    }

    getColorHex(colorName) {
      // Direct match
      if (colorMap[colorName]) {
        return colorMap[colorName];
      }

      // Partial match
      for (const [key, value] of Object.entries(colorMap)) {
        if (colorName.includes(key) || key.includes(colorName)) {
          return value;
        }
      }

      // Try to parse as hex
      if (colorName.startsWith('#')) {
        return colorName;
      }

      // Try to parse as RGB
      const rgbMatch = colorName.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (rgbMatch) {
        return colorName;
      }

      // Default fallback
      return '#9ca3af';
    }
  }

  // Initialize the color swatch filter
  new ColorSwatchFilter();

})();
